import { ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';
import swal from 'sweetalert2';
import { forkJoin, Subject } from 'rxjs';
import { Router } from '@angular/router';

import { AuthService } from '../../auth/AuthService';
import { InsuranceService, BenefitDomain } from '../../Services/insurance.service';
import { UserService } from '../../Services/UserService';

type AuditStatus = 'PENDING' | 'IN_AUDIT' | 'CHANGES_REQUESTED' | 'PASSED' | 'PUBLISHED';
type AuditPriority = 'LOW' | 'MEDIUM' | 'HIGH';

interface AuditQueueRow {
  id: number;
  version: number;
  revisionNumber: number;
  patientId: number;
  patientName: string;
  patientReference: string;
  insuranceSelection: string;
  formType: string;
  benefitDomain: BenefitDomain;
  submittedById: string | null;
  submittedBy: string;
  assignedAuditorId: string | null;
  auditor: string;
  submittedAt: string;
  completedAt: string | null;
  priority: AuditPriority;
  status: AuditStatus;
  score: number | null;
}

interface AuditorOption {
  keycloakId: string;
  fullName: string;
}

@Component({
  selector: 'pm-audit-queue',
  templateUrl: './audit-queue.component.html',
  styleUrls: ['./audit-queue.component.css']
})
export class AuditQueueComponent implements OnInit, OnDestroy {
  rows: AuditQueueRow[] = [];
  auditors: AuditorOption[] = [];
  selectedAuditIds: number[] = [];
  assignmentAuditor = '';
  totalPagesArray: number[] = [];
  private searchSubject = new Subject<string>();

  searchText = '';
  selectedStatus = '';
  selectedPriority = '';
  selectedFormType = '';
  selectedBenefitDomain: BenefitDomain | '' = '';
  selectedAuditor = '';
  fromDate = '';
  toDate = '';

  draftStatus = '';
  draftPriority = '';
  draftFormType = '';
  draftBenefitDomain: BenefitDomain | '' = '';
  draftAuditor = '';
  draftFromDate = '';
  draftToDate = '';

  isFilterOpen = false;

  @ViewChild('filterContainer') filterContainer!: ElementRef;

  page = 0;
  size = 25;
  totalRecords = 0;
  loading = false;
  metricsLoading = false;
  assigning = false;

  metrics = {
    totalAudits: 0,
    pending: 0,
    inAudit: 0,
    changesRequested: 0,
    qualityScore: '-',
    averageTat: '-'
  };

  readonly statuses: { value: AuditStatus; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_AUDIT', label: 'In Audit' },
    { value: 'CHANGES_REQUESTED', label: 'Changes Requested' },
    { value: 'PASSED', label: 'Passed' },
    { value: 'PUBLISHED', label: 'Published' }
  ];

  readonly priorities: AuditPriority[] = ['HIGH', 'MEDIUM', 'LOW'];
  readonly formTypes = ['FULL FORM', 'SHORT FORM', 'ELIG CHECK'];

  private clientSub: any;
  private currentClientId: number | null = null;
  private readonly queueStatePrefix = 'auditQueueState:';
  private queueRequestVersion = 0;

  constructor(
    private insuranceService: InsuranceService,
    private userService: UserService,
    public authService: AuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentClientId = this.authService.getClientId() || null;
    this.initializeDefaultDateRange();
    this.restoreQueueState();

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.page = 0;
      this.saveQueueState();
      this.loadAuditQueue();
    });

    this.clientSub = this.authService.clientIdSubject.subscribe((clientId: number | null) => {
      if (!clientId) {
        this.rows = [];
        this.totalRecords = 0;
        this.auditors = [];
        return;
      }

      if (clientId !== this.currentClientId) {
        this.currentClientId = clientId;
        this.resetQueueViewDefaults();
        this.restoreQueueState();
      }

      this.loadAuditQueue();
      this.loadAuditMetrics();
      this.loadAuditors();
    });
  }

  ngOnDestroy(): void {
    this.clientSub?.unsubscribe();
    this.searchSubject.complete();
  }

  private get queueStateKey(): string {
    const clientId =
      this.currentClientId ||
      this.authService.getClientId() ||
      'default';

    return `${this.queueStatePrefix}${clientId}`;
  }

  private resetQueueViewDefaults(): void {
    this.searchText = '';
    this.selectedStatus = '';
    this.selectedPriority = '';
    this.selectedFormType = '';
    this.selectedBenefitDomain = '';
    this.selectedAuditor = '';
    this.page = 0;
    this.size = 25;
    this.initializeDefaultDateRange();
  }

  private saveQueueState(): void {
    const state = {
      searchText: this.searchText,
      selectedStatus: this.selectedStatus,
      selectedPriority: this.selectedPriority,
      selectedFormType: this.selectedFormType,
      selectedBenefitDomain: this.selectedBenefitDomain,
      selectedAuditor: this.selectedAuditor,
      fromDate: this.fromDate,
      toDate: this.toDate,
      page: this.page,
      size: this.size
    };

    sessionStorage.setItem(this.queueStateKey, JSON.stringify(state));
  }

  private restoreQueueState(): void {
    const raw = sessionStorage.getItem(this.queueStateKey);
    if (!raw) {
      this.syncDraftFiltersFromApplied();
      return;
    }

    try {
      const state = JSON.parse(raw);

      this.searchText = state.searchText || '';
      this.selectedStatus = state.selectedStatus || '';
      this.selectedPriority = state.selectedPriority || '';
      this.selectedFormType = state.selectedFormType || '';
      this.selectedBenefitDomain = state.selectedBenefitDomain === 'DENTAL' || state.selectedBenefitDomain === 'MEDICAL'
        ? state.selectedBenefitDomain : '';
      this.selectedAuditor = state.selectedAuditor || '';
      this.fromDate = state.fromDate || this.fromDate;
      this.toDate = state.toDate || this.toDate;
      this.page = Number.isFinite(Number(state.page))
        ? Number(state.page)
        : 0;
      this.size = Number.isFinite(Number(state.size))
        ? Number(state.size)
        : 25;

      this.syncDraftFiltersFromApplied();
    } catch (error) {
      sessionStorage.removeItem(this.queueStateKey);
      this.syncDraftFiltersFromApplied();
    }
  }

  private initializeDefaultDateRange(): void {
    const today = new Date();
    const value =
      today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    this.fromDate = value;
    this.toDate = value;
    this.syncDraftFiltersFromApplied();
  }

  loadAuditMetrics(): void {
    const clientId = this.authService.getClientId();
    if (!clientId) {
      return;
    }

    const params = {
      clientId,
      userTimeZone: this.authService.getTimeZone(),
      from: this.toUtcStart(this.fromDate),
      to: this.toUtcExclusiveEnd(this.toDate)
    };

    this.metricsLoading = true;

    this.insuranceService.getAuditMetrics(params).pipe(first()).subscribe(
      (response: any) => {
        const totals = response?.totals ?? {};
        const queue = Array.isArray(response?.queue) ? response.queue : [];

        const queueCount = (status: string): number => {
          const item = queue.find((entry: any) => entry?.status === status);
          return Number(item?.count ?? 0);
        };

        this.ngZone.run(() => {
          this.metrics = {
            totalAudits: Number(totals?.total_audits ?? 0),
            pending: queueCount('PENDING'),
            inAudit: queueCount('IN_AUDIT'),
            changesRequested: queueCount('CHANGES_REQUESTED'),
            qualityScore:
              totals?.quality_score === null || totals?.quality_score === undefined
                ? '-'
                : `${Number(totals.quality_score).toFixed(1)}%`,
            averageTat: this.formatTat(totals?.average_tat_seconds)
          };

          this.metricsLoading = false;
          this.cdr.detectChanges();
        });
      },
      () => {
        this.ngZone.run(() => {
          this.metricsLoading = false;
          this.cdr.detectChanges();
        });
      }
    );
  }

  private formatTat(seconds: any): string {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
      return '-';
    }

    const totalSeconds = Math.max(0, Math.round(Number(seconds)));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  }

  loadAuditQueue(): void {
    const clientId = this.authService.getClientId();
    if (!clientId) {
      this.rows = [];
      this.totalRecords = 0;
      return;
    }

    const params = this.buildQueueParams();
    const requestVersion = ++this.queueRequestVersion;
    this.loading = true;

    this.insuranceService.getAuditQueue(params).pipe(first()).subscribe(
      (response: any) => {
        if (requestVersion !== this.queueRequestVersion) { return; }
        this.ngZone.run(() => {
          const content = response?.content ?? [];
          const actors = response?.actors ?? {};

          this.rows = content.map((item: any) => this.mapQueueRow(item, actors));
          this.totalRecords = Number(response?.total ?? 0);
          this.page = Number(response?.page ?? this.page);
          this.size = Number(response?.size ?? this.size);
          this.selectedAuditIds = [];
          this.calculateTotalPages();
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      (error: any) => {
        if (requestVersion !== this.queueRequestVersion) { return; }
        this.ngZone.run(() => {
          this.rows = [];
          this.totalRecords = 0;
          this.selectedAuditIds = [];
          this.calculateTotalPages();
          this.loading = false;
          this.cdr.detectChanges();
        });

        swal.fire({
          title: 'Unable to Load Audit Queue',
          icon: 'error',
          text: error?.error?.message || error?.error?.detail || 'The audit queue could not be loaded.',
          showConfirmButton: true
        });
      }
    );
  }

  private buildQueueParams(): any {
    const params: any = {
      clientId: this.authService.getClientId(),
      page: this.page,
      size: this.size,
      userTimeZone: this.authService.getTimeZone()
    };

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.selectedAuditor) {
      params.auditor = this.selectedAuditor;
    }

    if (this.selectedPriority) {
      params.priority = this.selectedPriority;
    }

    if (this.selectedFormType) {
      params.formType = this.selectedFormType;
    }
    if (this.selectedBenefitDomain) {
      params.benefitDomain = this.selectedBenefitDomain;
    }

    const search = this.searchText.trim();
    if (search) {
      params.search = search;
    }

    if (this.fromDate) {
      params.from = this.toUtcStart(this.fromDate);
    }

    if (this.toDate) {
      params.to = this.toUtcExclusiveEnd(this.toDate);
    }

    return params;
  }

  private toUtcStart(value: string): string {
    return new Date(`${value}T00:00:00.000Z`).toISOString();
  }

  private toUtcExclusiveEnd(value: string): string {
    const date = new Date(`${value}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString();
  }

  private mapQueueRow(item: any, actors: { [key: string]: string }): AuditQueueRow {
    const submittedById = item?.submitted_by ?? null;
    const assignedAuditorId = item?.assigned_auditor ?? null;

    return {
      id: Number(item?.id),
      version: Number(item?.version ?? 0),
      revisionNumber: Number(item?.revision_number ?? 0),
      patientId: Number(item?.patient_id),
      patientName: item?.patient_name || '-',
      patientReference: item?.patient_reference || '-',
      insuranceSelection: item?.insurance_selection || '-',
      formType: item?.form_type || '-',
      benefitDomain: item?.benefit_domain === 'MEDICAL' || item?.benefitDomain === 'MEDICAL' ? 'MEDICAL' : 'DENTAL',
      submittedById,
      submittedBy: submittedById ? (actors[submittedById] || submittedById) : '-',
      assignedAuditorId,
      auditor: assignedAuditorId ? (actors[assignedAuditorId] || assignedAuditorId) : 'Unassigned',
      submittedAt: item?.submitted_at || '',
      completedAt: item?.completed_at || null,
      priority: (item?.priority || 'MEDIUM') as AuditPriority,
      status: (item?.status || 'PENDING') as AuditStatus,
      score: item?.score === null || item?.score === undefined ? null : Number(item.score)
    };
  }

  loadAuditors(): void {
    const clientId = this.authService.getClientId();
    if (!clientId) {
      this.auditors = [];
      return;
    }

    this.userService.getAllUsers().pipe(first()).subscribe(
      (response: any) => {
        const users = Array.isArray(response)
          ? response
          : (response?.paginatedUser ?? []);

        this.ngZone.run(() => {
          this.auditors = users
          .filter((user: any) => {
            const clientIds = Array.isArray(user?.clientIds) ? user.clientIds.map(Number) : [];
            const roles = user?.roleGroup?.role ?? [];
            const hasAuditEdit = roles.some((role: any) => role?.name === 'audit-edit');

            return user?.active !== false &&
              user?.userType === 'INTERNAL' &&
              clientIds.includes(Number(clientId)) &&
              hasAuditEdit &&
              !!user?.userID;
          })
          .map((user: any) => ({
            keycloakId: user.userID,
            fullName: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.userName || user.userID
          }))
          .sort((a: AuditorOption, b: AuditorOption) => a.fullName.localeCompare(b.fullName));
          this.cdr.detectChanges();
        });
      },
      () => {
        this.ngZone.run(() => {
          this.auditors = [];
          this.cdr.detectChanges();
        });
      }
    );
  }

  private syncDraftFiltersFromApplied(): void {
    this.draftStatus = this.selectedStatus;
    this.draftPriority = this.selectedPriority;
    this.draftFormType = this.selectedFormType;
    this.draftBenefitDomain = this.selectedBenefitDomain;
    this.draftAuditor = this.selectedAuditor;
    this.draftFromDate = this.fromDate;
    this.draftToDate = this.toDate;
  }

  toggleFilterPanel(): void {
    if (this.isFilterOpen) {
      this.closeFilterPanel();
      return;
    }

    this.syncDraftFiltersFromApplied();
    this.isFilterOpen = true;
  }

  closeFilterPanel(): void {
    this.syncDraftFiltersFromApplied();
    this.isFilterOpen = false;
  }

  applyFilterPanel(): void {
    this.selectedStatus = this.draftStatus;
    this.selectedPriority = this.draftPriority;
    this.selectedFormType = this.draftFormType;
    this.selectedBenefitDomain = this.draftBenefitDomain;
    this.selectedAuditor = this.draftAuditor;
    this.fromDate = this.draftFromDate;
    this.toDate = this.draftToDate;

    this.page = 0;
    this.isFilterOpen = false;
    this.saveQueueState();
    this.loadAuditQueue();
    this.loadAuditMetrics();
  }

  resetFilterPanel(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.selectedStatus = '';
    this.selectedPriority = '';
    this.selectedFormType = '';
    this.selectedBenefitDomain = '';
    this.selectedAuditor = '';
    this.initializeDefaultDateRange();
    this.syncDraftFiltersFromApplied();
    this.page = 0;
    this.saveQueueState();
    this.loadAuditQueue();
    this.loadAuditMetrics();
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.selectedStatus) count++;
    if (this.selectedPriority) count++;
    if (this.selectedFormType) count++;
    if (this.selectedBenefitDomain) count++;
    if (this.selectedAuditor) count++;
    if (this.fromDate || this.toDate) count++;
    return count;
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (!this.isFilterOpen || !this.filterContainer) {
      return;
    }

    if (!this.filterContainer.nativeElement.contains(target)) {
      this.closeFilterPanel();
    }
  }

  applyFilters(): void {
    this.page = 0;
    this.saveQueueState();
    this.loadAuditQueue();
    this.loadAuditMetrics();
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value || '');
  }

  clearFilters(): void {
    this.searchText = '';
    this.resetFilterPanel();
  }

  isRowSelectable(row: AuditQueueRow): boolean {
    return this.authService.hasRole('audit-edit') &&
      (row.status === 'PENDING' || row.status === 'IN_AUDIT');
  }

  isRowSelected(auditId: number): boolean {
    return this.selectedAuditIds.includes(auditId);
  }

  toggleRowSelection(row: AuditQueueRow): void {
    if (!this.isRowSelectable(row)) {
      return;
    }

    const index = this.selectedAuditIds.indexOf(row.id);
    if (index === -1) {
      this.selectedAuditIds = [...this.selectedAuditIds, row.id];
    } else {
      this.selectedAuditIds = this.selectedAuditIds.filter(id => id !== row.id);
    }
  }

  toggleSelectAll(): void {
    const selectableIds = this.rows
      .filter(row => this.isRowSelectable(row))
      .map(row => row.id);

    const allSelected =
      selectableIds.length > 0 &&
      selectableIds.every(id => this.selectedAuditIds.includes(id));

    if (allSelected) {
      this.selectedAuditIds = this.selectedAuditIds.filter(
        id => !selectableIds.includes(id)
      );
    } else {
      this.selectedAuditIds = Array.from(
        new Set([...this.selectedAuditIds, ...selectableIds])
      );
    }
  }

  get allSelectableSelected(): boolean {
    const selectableIds = this.rows
      .filter(row => this.isRowSelectable(row))
      .map(row => row.id);

    return selectableIds.length > 0 &&
      selectableIds.every(id => this.selectedAuditIds.includes(id));
  }

  assignSelectedAudits(): void {
    if (!this.authService.hasRole('audit-edit')) {
      return;
    }

    if (!this.assignmentAuditor) {
      swal.fire({
        icon: 'warning',
        title: 'Select Auditor',
        text: 'Please select an auditor before assigning selected audits.'
      });
      return;
    }

    const selectedRows = this.rows.filter(
      row => this.selectedAuditIds.includes(row.id) && this.isRowSelectable(row)
    );

    if (!selectedRows.length) {
      swal.fire({
        icon: 'warning',
        title: 'Select Patients',
        text: 'Please select at least one pending or in-audit patient.'
      });
      return;
    }

    this.assigning = true;

    const calls = selectedRows.map(row =>
      this.insuranceService.assignAuditAuditor(
        row.id,
        {
          version: row.version,
          auditor: this.assignmentAuditor
        },
        {
          clientId: this.authService.getClientId()
        }
      ).pipe(first())
    );

    forkJoin(calls).subscribe(
      () => {
        this.ngZone.run(() => {
          this.assigning = false;
          this.selectedAuditIds = [];
          this.cdr.detectChanges();
        });

        swal.fire({
          icon: 'success',
          title: 'Auditor Assigned',
          text: `${selectedRows.length} audit${selectedRows.length === 1 ? '' : 's'} assigned successfully.`,
          showConfirmButton: false,
          timer: 1800
        });

        this.loadAuditQueue();
      },
      (error: any) => {
        this.ngZone.run(() => {
          this.assigning = false;
          this.cdr.detectChanges();
        });

        const message = error?.status === 409
          ? 'One or more audits changed after they were loaded. The queue has been refreshed.'
          : (error?.error?.message || error?.error?.detail || 'Unable to assign the selected audits.');

        swal.fire({
          icon: error?.status === 409 ? 'warning' : 'error',
          title: 'Assignment Failed',
          text: message
        });

        this.loadAuditQueue();
      }
    );
  }

  getActionLabel(status: AuditStatus): string {
    if (status === 'PASSED') {
      return 'Finalize';
    }

    return status === 'IN_AUDIT' || status === 'CHANGES_REQUESTED'
      ? 'Review'
      : 'Open';
  }

  openAudit(row: AuditQueueRow): void {
    this.saveQueueState();

    if (row.status === 'PASSED') {
      this.router.navigate(['/audit/finalization', row.id]);
      return;
    }

    this.router.navigate(['/audit/review', row.id]);
  }

  getAssignmentAuditorName(): string {
    if (!this.assignmentAuditor) {
      return 'Select Auditor';
    }

    const auditor = this.auditors.find(
      item => item.keycloakId === this.assignmentAuditor
    );

    return auditor?.fullName || 'Select Auditor';
  }

  getStatusLabel(status: AuditStatus): string {
    const match = this.statuses.find(item => item.value === status);
    return match ? match.label : status;
  }

  getStatusClass(status: AuditStatus): string {
    switch (status) {
      case 'PENDING': return 'status-pending';
      case 'IN_AUDIT': return 'status-progress';
      case 'CHANGES_REQUESTED': return 'status-change';
      case 'PASSED': return 'status-passed';
      case 'PUBLISHED': return 'status-published';
      default: return '';
    }
  }

  getPriorityClass(priority: AuditPriority): string {
    return `priority-${priority.toLowerCase()}`;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.size));
  }

  calculateTotalPages(): void {
    this.totalPagesArray = Array.from(
      { length: Math.ceil(this.totalRecords / this.size) },
      (_, i) => i + 1
    );
  }

  pageChange(event: any): void {
    this.page = event.pageIndex;
    this.size = event.pageSize;
    this.saveQueueState();
    this.loadAuditQueue();
  }

  jumpToPage(event: any): void {
    this.page = event.value;
    this.saveQueueState();
    this.loadAuditQueue();
  }

  trackById(index: number, row: AuditQueueRow): number {
    return row.id;
  }
}
